import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCRM } from '../context/CRMContext';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { TextArea } from '../components/ui/TextArea';
import { Contact, CompanyName, CompanyRelationship } from '../types/crm';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { formatCurrency } from '../utils/calculations';
const AVAILABLE_COMPANIES: CompanyName[] = ['TKDScore', 'Website Forge', 'Magnus Software', 'Merem Blasting', 'Mission Impossible'];
export function LeadForm() {
  const navigate = useNavigate();
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const {
    selectedCompany
  } = useData();
  const {
    addContact,
    updateContact,
    getContactById
  } = useCRM();
  const isEditMode = id && id !== 'new';
  const [formData, setFormData] = useState<Omit<Contact, 'id' | 'company_id' | 'created_at' | 'updated_at'>>({
    first_name: '',
    last_name: '',
    company: '',
    email_primary: '',
    email_secondary: '',
    phone_primary: '',
    phone_secondary: '',
    status: 'lead',
    companies: [],
    notes: ''
  });
  useEffect(() => {
    if (isEditMode && id) {
      const contact = getContactById(id);
      if (contact) {
        setFormData({
          first_name: contact.first_name,
          last_name: contact.last_name,
          company: contact.company || '',
          email_primary: contact.email_primary,
          email_secondary: contact.email_secondary || '',
          phone_primary: contact.phone_primary,
          phone_secondary: contact.phone_secondary || '',
          status: contact.status,
          companies: contact.companies,
          notes: contact.notes || ''
        });
      }
    }
  }, [isEditMode, id, getContactById]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompany) return;
    if (isEditMode && id) {
      updateContact(id, formData);
    } else {
      addContact({
        ...formData,
        company_id: selectedCompany.id
      });
    }
    navigate('/lead-flow');
  };
  const addCompanyRelationship = () => {
    setFormData({
      ...formData,
      companies: [...formData.companies, {
        company_name: 'TKDScore',
        last_purchase_date: null,
        lifetime_purchase: 0
      }]
    });
  };
  const removeCompanyRelationship = (index: number) => {
    setFormData({
      ...formData,
      companies: formData.companies.filter((_, i) => i !== index)
    });
  };
  const updateCompanyRelationship = (index: number, field: keyof CompanyRelationship, value: any) => {
    const updatedCompanies = [...formData.companies];
    updatedCompanies[index] = {
      ...updatedCompanies[index],
      [field]: value
    };
    setFormData({
      ...formData,
      companies: updatedCompanies
    });
  };
  return <div className="max-w-3xl mx-auto space-y-6" data-id="element-1803">
      <div className="flex items-center gap-4" data-id="element-1804">
        <Button variant="ghost" onClick={() => navigate('/lead-flow')} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-1806" />} size="sm" data-id="element-1805">
          Back
        </Button>
        <div data-id="element-1807">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1808">
            {isEditMode ? 'Edit Contact' : 'New Contact'}
          </h1>
          <p className="text-sm text-gray-600" data-id="element-1809">
            {isEditMode ? 'Update contact details' : 'Add a new lead or customer'}
          </p>
        </div>
      </div>

      <Card data-id="element-1810">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1811">
          {/* Basic Info */}
          <div data-id="element-1812">
            <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-1813">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1814">
              <Input label="First Name" value={formData.first_name} onChange={e => setFormData({
              ...formData,
              first_name: e.target.value
            })} required placeholder="e.g. John" data-id="element-1815" />
              <Input label="Last Name" value={formData.last_name} onChange={e => setFormData({
              ...formData,
              last_name: e.target.value
            })} required placeholder="e.g. Doe" data-id="element-1816" />
            </div>

            <div className="mt-4" data-id="element-1817">
              <Input label="Company" value={formData.company} onChange={e => setFormData({
              ...formData,
              company: e.target.value
            })} placeholder="e.g. Acme Corp" data-id="element-1818" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4" data-id="element-1819">
              <Input label="Primary Email" type="email" value={formData.email_primary} onChange={e => setFormData({
              ...formData,
              email_primary: e.target.value
            })} required placeholder="john@example.com" data-id="element-1820" />
              <Input label="Secondary Email (Optional)" type="email" value={formData.email_secondary} onChange={e => setFormData({
              ...formData,
              email_secondary: e.target.value
            })} placeholder="alt@example.com" data-id="element-1821" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4" data-id="element-1822">
              <Input label="Primary Phone" type="tel" value={formData.phone_primary} onChange={e => setFormData({
              ...formData,
              phone_primary: e.target.value
            })} required placeholder="(555) 123-4567" data-id="element-1823" />
              <Input label="Secondary Phone (Optional)" type="tel" value={formData.phone_secondary} onChange={e => setFormData({
              ...formData,
              phone_secondary: e.target.value
            })} placeholder="(555) 987-6543" data-id="element-1824" />
            </div>

            <div className="mt-4" data-id="element-1825">
              <Select label="Status" value={formData.status} onChange={e => setFormData({
              ...formData,
              status: e.target.value as Contact['status']
            })} options={[{
              value: 'lead',
              label: 'Lead'
            }, {
              value: 'contact',
              label: 'Contact'
            }, {
              value: 'customer',
              label: 'Customer'
            }, {
              value: 'lost',
              label: 'Lost'
            }]} data-id="element-1826" />
            </div>
          </div>

          {/* Company Relationships */}
          <div className="pt-6 border-t border-gray-200" data-id="element-1827">
            <div className="flex items-center justify-between mb-4" data-id="element-1828">
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-1829">
                Company Relationships
              </h3>
              <Button type="button" variant="secondary" size="sm" onClick={addCompanyRelationship} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1831" />} data-id="element-1830">
                Add Company
              </Button>
            </div>

            {formData.companies.length === 0 ? <p className="text-sm text-gray-500 italic" data-id="element-1832">
                No company relationships yet
              </p> : <div className="space-y-4" data-id="element-1833">
                {formData.companies.map((companyRel, index) => <div key={index} className="p-4 bg-gray-50 rounded-lg space-y-3" data-id="element-1834">
                    <div className="flex items-start justify-between" data-id="element-1835">
                      <Select label="Company" value={companyRel.company_name} onChange={e => updateCompanyRelationship(index, 'company_name', e.target.value as CompanyName)} options={AVAILABLE_COMPANIES.map(name => ({
                  value: name,
                  label: name
                }))} data-id="element-1836" />
                      <button type="button" onClick={() => removeCompanyRelationship(index)} className="ml-2 mt-6 p-2 text-gray-400 hover:text-red-600 transition-colors" title="Remove" data-id="element-1837">
                        <TrashIcon className="w-4 h-4" data-id="element-1838" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1839">
                      <Input label="Last Purchase Date" type="date" value={companyRel.last_purchase_date ? companyRel.last_purchase_date.toISOString().split('T')[0] : ''} onChange={e => updateCompanyRelationship(index, 'last_purchase_date', e.target.value ? new Date(e.target.value) : null)} data-id="element-1840" />
                      <Input label="Lifetime Purchase ($)" type="number" step="0.01" value={companyRel.lifetime_purchase} onChange={e => updateCompanyRelationship(index, 'lifetime_purchase', parseFloat(e.target.value) || 0)} placeholder="0.00" data-id="element-1841" />
                    </div>
                  </div>)}
              </div>}
          </div>

          {/* Notes */}
          <div className="pt-6 border-t border-gray-200" data-id="element-1842">
            <TextArea label="Notes" value={formData.notes} onChange={e => setFormData({
            ...formData,
            notes: e.target.value
          })} placeholder="Add any notes about this contact..." rows={4} data-id="element-1843" />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100" data-id="element-1844">
            <Button type="button" variant="secondary" onClick={() => navigate('/lead-flow')} data-id="element-1845">
              Cancel
            </Button>
            <Button type="submit" data-id="element-1846">
              {isEditMode ? 'Update Contact' : 'Create Contact'}
            </Button>
          </div>
        </form>
      </Card>
    </div>;
}